import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

// Connects to data-controller="search-input"
export default class extends Controller<HTMLInputElement> {
  static values = {
    action: String,
    frame: String,
    url: String,
    debounce: Number,
    preserveParams: Boolean,
  };

  defaultDebounce = 250;
  // The frame also loads for filters, pages and dates; the spinner is only for a search this box sent.
  private searching = false;

  declare timeout: NodeJS.Timeout | undefined;
  declare observer: MutationObserver | undefined;

  // declare element: HTMLInputElement
  declare urlValue: string;
  declare actionValue: string;
  declare frameValue: string;
  declare debounceValue: number | undefined;
  declare preserveParamsValue: boolean;

  connect() {
    this.element.addEventListener("input", this.onInput);
    this.element.addEventListener("keydown", this.blockImplicitSubmit);
    this.createSpinner();
    this.observeBusyState();
  }

  disconnect() {
    this.element.removeEventListener("input", this.onInput);
    this.element.removeEventListener("keydown", this.blockImplicitSubmit);
    this.removeSpinner();
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  createSpinner() {
    // Create spinner element if it doesn't exist
    if (!this.element.parentElement?.querySelector(".search-spinner")) {
      const spinner = document.createElement("div");
      spinner.className =
        "search-spinner absolute right-3 top-1/2 transform -translate-y-1/2 hidden opacity-0 transition-opacity duration-200";
      spinner.innerHTML = `
        <svg class="animate-spin h-5 w-5 text-slate-900" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
        </svg>
      `;

      // Make sure the input container has relative positioning
      if (this.element.parentElement) {
        this.element.parentElement.style.position = "relative";
        this.element.parentElement.appendChild(spinner);
      }
    }
  }

  removeSpinner() {
    const spinner =
      this.element.parentElement?.querySelector(".search-spinner");
    if (spinner) {
      spinner.remove();
    }
  }

  observeBusyState() {
    // Find the turbo frame that this search input targets
    const frame = document.querySelector(`turbo-frame#${this.frameValue}`);
    if (frame) {
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "busy"
          ) {
            this.updateSpinnerVisibility();
          }
        });
      });

      this.observer.observe(frame, { attributes: true });
      // Initial check
      this.updateSpinnerVisibility();
    }
  }

  updateSpinnerVisibility() {
    const frame = document.querySelector(`turbo-frame#${this.frameValue}`);
    const spinner =
      this.element.parentElement?.querySelector(".search-spinner");

    if (spinner && frame) {
      const busy = frame.hasAttribute("busy");
      if (!busy) this.searching = false;
      if (busy && this.searching) {
        // Show spinner
        spinner.classList.remove("hidden");
        spinner.classList.add("block");
        spinner.classList.remove("opacity-0");
        spinner.classList.add("opacity-100");

        // Hide cancel button
        this.hideCancelButton();
      } else {
        // Hide spinner
        spinner.classList.add("hidden");
        spinner.classList.remove("block");
        spinner.classList.remove("opacity-100");
        spinner.classList.add("opacity-0");

        // Show cancel button
        this.showCancelButton();
      }
    }
  }

  hideCancelButton() {
    // Add a class to hide the cancel button
    this.element.classList.add("hide-cancel-button");
  }

  showCancelButton() {
    // Remove the class to show the cancel button
    this.element.classList.remove("hide-cancel-button");
  }

  // Searching inside a form (the directory-listing sheet) would otherwise submit
  // it on Enter, answering the question with whatever was typed so far. Results
  // arrive on their own; Enter has nothing to do here.
  onInput = () => this.search();

  blockImplicitSubmit = (event: KeyboardEvent) => {
    if (event.key === "Enter" && this.element.form) event.preventDefault();
  };

  updateUrl(url: string) {
    this.urlValue = url;
  }

  search() {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      const url = new URL(this.urlValue, document.baseURI);

      if (this.preserveParamsValue) {
        // Get all existing params to preserve them
        const existingParams = new URLSearchParams(window.location.search);
        // Set the search query parameter
        existingParams.set("q", this.element.value);
        // Replace the URL's search with our updated params
        url.search = existingParams.toString();
      } else {
        url.searchParams.set("q", this.element.value);
      }

      this.searching = true;
      this.refocusAfterRender();
      Turbo.visit(url.toString(), {
        action: this.actionValue,
        frame: this.frameValue,
      });
    }, this.debounceValue || this.defaultDebounce);
  }

  // A box inside the frame it drives is replaced by the render, taking the
  // caret with it. Hand focus to its successor so typing carries on.
  private refocusAfterRender() {
    const frame = document.querySelector(`turbo-frame#${this.frameValue}`);
    const id = this.element.id;
    if (!frame || !id || !frame.contains(this.element) || document.activeElement !== this.element) return;

    frame.addEventListener(
      "turbo:frame-render",
      () => {
        const next = frame.querySelector<HTMLInputElement>(`[id="${id}"]`);
        if (!next || next === this.element) return;
        next.focus();
        next.setSelectionRange(next.value.length, next.value.length);
      },
      { once: true },
    );
  }
}
